import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import glossaryHelper from '@/helpers/glossaryHelper'
import { indexDbHelper, TranslationTextCache } from '@/helpers/indexDbHelper'
import { TranslateServiceType } from '@/services/translateService'
import { TranslateServiceConsts } from '@/services/translateService/consts'
import { userService } from '@/services/userService'
import { toJS } from 'mobx'
import { PdfProTranslateServiceType as Type } from './serviceTypes'

type Locale = Type.Locale
type Paragraph = Type.Paragraph
type TranslateResult = Type.TranslateResult
type TriggerType = PostDataTypes.UseType
type TranslateOptions = Type.TranslateOptions

interface ParagraphGroup {
  merged: boolean
  mergedText: string
  paragraphs: Array<Paragraph>
  textType: 'plain'
  // 前端检测的段落语言
  detectLang: Locale
}

export class PdfProTranslateService {
  private storageWarningShown = false

  public async translate(
    {
      from,
      to,
      paragraphs,
      triggerType,
      taskUid,
      originTitle,
      originUrl,
      context,
      model,
      action,
      glossaryMode,
      contextType,
    }: {
      paragraphs: Array<Paragraph>
      taskUid: string
      originUrl: string
      originTitle: string
      triggerType: TriggerType
      from: string
      to: string
      model: ServerDataTypes.Model
      context?: string
      action: 'translate' | 'download'
      glossaryMode: TranslateServiceType.GlossaryMode
      contextType: PostDataTypes.ContextType
    },
    callback: (results: Array<TranslateResult>) => void
  ): Promise<void> {
    if (!paragraphs || paragraphs.length <= 0) {
      callback([])
      return
    }
    const { fromLang, toLang } = this.normalizeLocale({
      from,
      to,
    })
    const paragraphGroups = this.mergeParagraph(paragraphs)
    await this.translateParagraphGroups({
      originTitle,
      originUrl,
      paragraphGroups,
      taskUid,
      triggerType,
      to: toLang,
      from: fromLang,
      context,
      model,
      callback,
      action,
      glossaryMode,
      contextType,
    })
    // this.updateUsageInfoThrottleCal()
  }

  private async translateParagraphGroups({
    paragraphGroups,
    originTitle,
    originUrl,
    taskUid,
    triggerType,
    from,
    to,
    context,
    model,
    callback,
    action,
    glossaryMode,
    contextType,
  }: {
    paragraphGroups: Array<ParagraphGroup>
    taskUid: string
    originUrl: string
    originTitle: string
    triggerType: TriggerType
    from: string
    to: string
    context?: string
    model: ServerDataTypes.Model
    callback: (results: Array<TranslateResult>) => void
    action: 'translate' | 'download'
    glossaryMode: TranslateServiceType.GlossaryMode
    contextType: PostDataTypes.ContextType
  }): Promise<void> {
    await Promise.all(
      paragraphGroups.map(async (s) => {
        return this.translateParagraphGroup({
          pageTitle: originTitle,
          pageUrl: originUrl,
          paragraphGroup: s,
          taskUid,
          triggerType,
          detectLang: s.detectLang,
          from,
          to,
          context,
          model,
          callback,
          action,
          glossaryMode,
          contextType,
        })
      })
    )
  }

  private async translateParagraphGroup({
    pageTitle,
    pageUrl,
    paragraphGroup,
    taskUid,
    triggerType,
    detectLang,
    from,
    to,
    context,
    callback,
    model,
    action,
    glossaryMode,
    contextType,
  }: {
    action: 'translate' | 'download'
    paragraphGroup: ParagraphGroup
    taskUid: string
    pageTitle: string
    pageUrl: string
    triggerType: TriggerType
    detectLang: Type.Locale
    from: string
    to: string
    context?: string
    model: ServerDataTypes.Model
    callback: (results: Array<TranslateResult>) => void
    glossaryMode: TranslateServiceType.GlossaryMode
    contextType: PostDataTypes.ContextType
  }): Promise<void> {
    const { textType, paragraphs } = paragraphGroup
    const options: TranslateOptions = {
      fromLang: from,
      taskUid,
      textList: paragraphs.map((p) => {
        return p.text
      }),
      toLang: to,
      textType,
      pageUrl,
      pageTitle,
      translateType: 'ai',
      detectLang,
      context,
      translateModel: model.key,
    }

    await this.tryTranslateByServer({
      options,
      paragraphs,
      triggerType,
      callback,
      from,
      to,
      model,
      action,
      glossaryMode,
      contextType,
    })
  }

  private async tryTranslateByServer({
    options,
    paragraphs,
    triggerType,
    from,
    to,
    callback,
    model,
    action,
    glossaryMode,
    contextType,
  }: {
    options: TranslateOptions
    paragraphs: Paragraph[]
    triggerType: TriggerType
    from: string
    to: string
    callback: (results: Array<TranslateResult>) => void
    model: ServerDataTypes.Model
    action: 'translate' | 'download'
    glossaryMode: TranslateServiceType.GlossaryMode
    contextType: PostDataTypes.ContextType
  }): Promise<void> {
    const glossaryData = await userService.getGlossaryData({
      fromLang: from,
      toLang: to,
      glossaryMode,
    })
    // 启用了术语表，则进行术语表预处理
    console.log('@@@@glossaryMode', glossaryMode, toJS(glossaryData))
    paragraphs.forEach((p) => {
      const preprocessResult = glossaryHelper.preprocessText(p.text, glossaryData)
      p.text = preprocessResult.processedText
      p.preprocessResult = preprocessResult
    })

    const results: Array<TranslateResult> = []
    // const { cachedResults, needServiceTranslateParagraphs } = await this.tryGetCachedParagraphs({
    //   paragraphs,
    //   from,
    //   to,
    //   triggerType,
    //   model,
    //   contextType,
    // })

    const needServiceTranslateParagraphs = paragraphs
    const cachedResults: Array<TranslateResult> = []

    // 命中缓存后，如果术语表有替换，则需要进行后处理
    if (cachedResults.length > 0) {
      // console.log('@@@@cachedResults', cachedResults)
      cachedResults.forEach((cacheItem) => {
        if (cacheItem.preprocessResult) {
          const postTextResult = glossaryHelper.postprocessText(
            cacheItem.translateResult,
            cacheItem.preprocessResult
          )
          cacheItem.translateResult = postTextResult.finalText
          // console.log('@@@@postTextResult', postTextResult)
        }
      })
    }

    // 获取缓存数据
    callback(cachedResults)
    if (needServiceTranslateParagraphs.length === 0) {
      callback([])
      return
    }
    const translatedParagraphs: Array<TranslateResult> =
      needServiceTranslateParagraphs as Array<TranslateResult>

    await this.translateByServer({
      options: {
        ...options,
        textList: needServiceTranslateParagraphs.map((p) => {
          return p.text
        }),
      },
      triggerType,
      model,
      action,
      callback: (err, data, taskId) => {
        if (err) {
          const errMsg = commonUtils.formatErrorMsg(err) || '翻译失败，请稍后重试或联系客服'
          results.push(
            ...translatedParagraphs
              .filter((p) => !p.translateResult)
              .map<TranslateResult>((p) => {
                return {
                  id: p.id,
                  text: p.text,
                  detectLang: p.detectLang,
                  translateResult: '',
                  errMsg,
                  taskId,
                }
              })
          )
          callback(results)
        } else {
          const cacheList: Array<{ p: Paragraph; cacheData: string; taskId: number }> = []
          results.push(
            ...data.map<TranslateResult>(({ data, index }) => {
              const p = translatedParagraphs[index]
              p.translateResult = data
              if (p.preprocessResult) {
                p.translateResult = glossaryHelper.postprocessText(
                  p.translateResult,
                  p.preprocessResult
                ).finalText
              }
              if (data) {
                cacheList.push({
                  p,
                  cacheData: data,
                  taskId: taskId!,
                })
              }

              return {
                id: p.id,
                text: p.text,
                detectLang: p.detectLang,
                translateResult: p.translateResult,
                errMsg: null,
                taskId: taskId!,
              }
            })
          )
          callback(results)
          // 更新缓存数据
          // this.setTranslateToCache(cacheList, from, to, triggerType, model, contextType)
        }
      },
    })
  }

  private async translateByServer({
    options,
    triggerType,
    callback,
    model,
    action,
  }: {
    options: TranslateOptions
    triggerType: TriggerType
    callback: (
      err: string | null,
      results: Array<ServerDataTypes.AiTranslateResult>,
      taskId?: number
    ) => void
    model: ServerDataTypes.Model
    action: 'translate' | 'download'
  }) {
    if (model.type === 'machine') {
      await this.translateByXunFeiTranslate({
        options,
        triggerType,
        callback,
      })
    }

    if (model.type === 'ai') {
      if (action === 'translate') {
        await this.translateByAiTranslateV2({ model, options, triggerType, callback })
      } else {
        await this.translateByAiTranslateBatch({ model, options, triggerType, callback })
      }
    }
  }

  private async translateByAiTranslateBatch({
    model,
    options,
    triggerType,
    callback,
  }: {
    model: ServerDataTypes.Model
    options: TranslateOptions
    triggerType: PostDataTypes.UseType
    callback: (
      err: string | null,
      results: Array<ServerDataTypes.AiTranslateResult>,
      taskId?: number
    ) => void
  }) {
    // try {

    //   const { respContentList } = await api.pdf.translatePdfPro({
    //     ...options,
    //     useType: triggerType,
    //   })
    //   callback(
    //     null,
    //     respContentList.map((data, index) => {
    //       return {
    //         data,
    //         index,
    //       }
    //     })
    //   )
    // } catch (error) {
    //   callback(commonUtils.formatErrorMsg(error), [])
    // }

    try {
      const res = await api.pdf.batchAiTranslate({
        textList: options.textList,
        fromLang: options.fromLang,
        toLang: options.toLang,
        wordAiType: 'normal',
        useType: triggerType,
        translateModel: model.key,
      })

      callback(
        null,
        res.aiTransContentList.map((data, index) => {
          return {
            data,
            index,
          }
        })
      )
    } catch (error) {
      callback(commonUtils.formatErrorMsg(error), [])
    }
  }

  private async translateByAiTranslateV2({
    model,
    options,
    triggerType,
    callback,
  }: {
    model: ServerDataTypes.Model
    options: TranslateOptions
    triggerType: PostDataTypes.UseType
    callback: (
      err: string | null,
      results: Array<ServerDataTypes.AiTranslateResult>,
      taskId?: number
    ) => void
  }) {
    const maxTotalConcurrent = 10 // 总并发数限制
    const batchSize = 5 // API最大支持5个文本
    const longTextThreshold = 100 // 定义长文本阈值（字符数）

    try {
      const tasks = options.textList.map((text, index) => ({ text, index }))

      // 分离长文本和短文本
      const longTextTasks = tasks.filter((task) => task.text.length > longTextThreshold)
      const shortTextTasks = tasks.filter((task) => task.text.length <= longTextThreshold)

      // 创建所有任务
      const allTasks: Array<() => Promise<any>> = []

      // 添加长文本任务（每个长文本一个任务）
      longTextTasks.forEach((task) => {
        allTasks.push(() =>
          this.processLongText({
            task,
            options,
            triggerType,
            model,
            callback,
          })
        )
      })

      // 添加短文本批处理任务（每个批次一个任务）
      if (shortTextTasks.length > 0) {
        const shortTextBatches = this.createShortTextBatches(shortTextTasks, batchSize)
        shortTextBatches.forEach((batch) => {
          allTasks.push(() =>
            this.processShortTextBatch({
              batch,
              options,
              triggerType,
              model,
              callback,
            })
          )
        })
      }

      // 使用并发控制器执行所有任务
      await this.executeConcurrentTasks(allTasks, maxTotalConcurrent)
    } catch (error) {
      callback(commonUtils.formatErrorMsg(error), [])
    }
  }

  /**
   * 并发控制器 - 控制总并发数
   */
  private async executeConcurrentTasks(
    tasks: Array<() => Promise<any>>,
    maxConcurrent: number
  ): Promise<void> {
    const executing: Promise<any>[] = []

    for (const task of tasks) {
      const promise = task().then(() => {
        // 任务完成后从执行队列中移除
        const index = executing.indexOf(promise)
        if (index > -1) {
          executing.splice(index, 1)
        }
      })

      executing.push(promise)

      // 如果达到最大并发数，等待其中一个完成
      if (executing.length >= maxConcurrent) {
        await Promise.race(executing)
      }
    }

    // 等待所有剩余任务完成
    await Promise.allSettled(executing)
  }

  /**
   * 处理短文本批次
   */
  private async processShortTextBatch({
    batch,
    options,
    triggerType,
    model,
    callback,
  }: {
    batch: Array<{ text: string; index: number }>
    options: TranslateOptions
    triggerType: PostDataTypes.UseType
    model: ServerDataTypes.Model
    callback: (
      err: string | null,
      results: Array<ServerDataTypes.AiTranslateResult>,
      taskId?: number
    ) => void
  }): Promise<any> {
    try {
      const textList = batch.map((item) => item.text)
      const res = await api.pdf.aiTranslateWithContext({
        textList, // 传递文本数组
        toLang: options.toLang,
        useType: triggerType,
        wordAiType: 'normal',
        translateModel: model.key,
        context: options.context,
        timeout: 20, // 短文本使用标准超时时间
      })

      // 处理批量返回的结果
      const results = batch.map((item, idx) => ({
        // 有可能返回的数组长度小于batch的长度，这样的情况下返回空字符串
        data: res.aiTransContentList[idx] ?? '',
        index: item.index,
        errMsg: undefined,
      }))

      callback(null, results)
      return results
    } catch (error) {
      const results = batch.map((item) => ({
        data: '',
        index: item.index,
        errMsg: commonUtils.formatErrorMsg(error),
      }))

      callback(commonUtils.formatErrorMsg(error), results)
      return results
    }
  }

  /**
   * 创建短文本批次
   */
  private createShortTextBatches(
    shortTextTasks: Array<{ text: string; index: number }>,
    batchSize: number
  ): Array<Array<{ text: string; index: number }>> {
    const batches = []
    for (let i = 0; i < shortTextTasks.length; i += batchSize) {
      batches.push(shortTextTasks.slice(i, i + batchSize))
    }
    return batches
  }

  /**
   * 处理单个长文本
   */
  private async processLongText({
    task,
    options,
    triggerType,
    model,
    callback,
  }: {
    task: { text: string; index: number }
    options: TranslateOptions
    triggerType: PostDataTypes.UseType
    model: ServerDataTypes.Model
    callback: (
      err: string | null,
      results: Array<ServerDataTypes.AiTranslateResult>,
      taskId?: number
    ) => void
  }): Promise<any> {
    try {
      const res = await api.pdf.aiTranslateWithContext({
        textList: [task.text], // 单个文本
        toLang: options.toLang,
        useType: triggerType,
        wordAiType: 'normal',
        translateModel: model.key,
        context: options.context,
        timeout: 40, // 长文本使用更长的超时时间
      })

      const result = {
        data: res.aiTransContentList[0] ?? '',
        index: task.index,
        errMsg: undefined,
      }

      callback(null, [result])
      return result
    } catch (error) {
      const result = {
        data: '',
        index: task.index,
        errMsg: commonUtils.formatErrorMsg(error),
      }

      callback(commonUtils.formatErrorMsg(error), [result])
      return result
    }
  }

  private async translateByXunFeiTranslate({
    options,
    triggerType,
    callback,
  }: {
    options: TranslateOptions
    triggerType: TriggerType
    callback: (
      err: string | null,
      results: Array<ServerDataTypes.AiTranslateResult>,
      taskId?: number
    ) => void
  }) {
    try {
      //   const useTranslateCache = await this.services.sync.get('useTranslateCache')

      const { respContentList } = await api.pdf.translatePdfPro({
        ...options,
        useType: triggerType,
      })
      callback(
        null,
        respContentList.map((data, index) => {
          return {
            data,
            index,
          }
        })
      )
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      //   api.recordError(`xunfei_${commonUtils.formatErrorMsg(error)}`, {
      //     ...options,
      //     useType: triggerType,
      //   })
      callback(commonUtils.formatErrorMsg(error), [])
    }
  }

  private async tryGetCachedParagraphs({
    paragraphs,
    from,
    to,
    triggerType,
    model,
    contextType,
  }: {
    paragraphs: Array<Paragraph>
    from: string
    to: string
    triggerType: TriggerType
    model: ServerDataTypes.Model
    contextType: PostDataTypes.ContextType
  }): Promise<{
    needServiceTranslateParagraphs: Array<Paragraph>
    cachedResults: Array<TranslateResult>
  }> {
    const needServiceTranslateParagraphs: Array<Paragraph> = []
    const cachedResults: Array<TranslateResult> = []

    // 1. 批量生成所有缓存键，建立键与段落的映射关系
    const paragraphKeyMap = new Map<string, Paragraph>()
    const cacheKeys = paragraphs.map((p) => {
      const key = this.getParagraphCacheKey({ p, from, to, model, contextType })
      paragraphKeyMap.set(key, p)
      return key
    })

    // 2. 批量查询缓存 - 一次查询替代 N 次查询
    const cachedData = await indexDbHelper.batchGetTextByKeys(cacheKeys)

    // 3. 创建查询结果映射，便于快速查找
    const cachedMap = new Map<string, TranslationTextCache>()
    cachedData.forEach((item) => {
      cachedMap.set(item.cacheKey, item)
    })

    // 4. 处理结果 - 遍历原始段落，检查缓存命中情况
    paragraphs.forEach((p) => {
      const key = this.getParagraphCacheKey({ p, from, to, model, contextType })
      const cached = cachedMap.get(key)

      if (cached) {
        // 缓存命中，直接使用缓存结果
        cachedResults.push({
          id: p.id,
          text: p.text,
          detectLang: p.detectLang,
          translateResult: cached.translatedText,
          errMsg: null,
          preprocessResult: p.preprocessResult,
        })
      } else {
        // 缓存未命中，需要调用翻译服务
        needServiceTranslateParagraphs.push(p)
      }
    })

    return {
      needServiceTranslateParagraphs,
      cachedResults,
    }
  }

  private getParagraphCacheKey({
    p,
    from,
    to,
    model,
    contextType,
  }: {
    p: Paragraph
    from: string
    to: string
    model: ServerDataTypes.Model
    contextType: PostDataTypes.ContextType
  }): string {
    const md5 = commonUtils.md5(`$ai-${to}-${from}-${p.text}-${model.key}-${contextType}`)
    return md5.substring(8, 24)
  }

  private mergeParagraph(paragraphs: Array<Paragraph>): Array<ParagraphGroup> {
    const paragraphGroups: Array<ParagraphGroup> = []
    // 限制详见 https://learn.microsoft.com/zh-cn/azure/cognitive-services/Translator/service-limits#character-and-array-limits-per-request
    // 一段 paragraphGroup 最多包含 40 * 1000 个字符
    const maxMergedTextLength = 40 * 1000
    // 一段 paragraphGroup 最多包含 100 个 payloads
    const maxParagraphCount = 1000
    const pushNewParagraphGroup = (paragraph: Paragraph) => {
      paragraphGroups.push({
        merged: false,
        mergedText: paragraph.text,
        paragraphs: [paragraph],
        textType: 'plain',
        detectLang: paragraph.detectLang,
      })
    }
    paragraphs.forEach((paragraph) => {
      const matchedGroup = this.findMatchedParagraphGroup(paragraph, paragraphGroups)
      if (matchedGroup) {
        if (
          matchedGroup.paragraphs.length < maxParagraphCount &&
          matchedGroup.mergedText.length + paragraph.text.length < maxMergedTextLength
        ) {
          matchedGroup.paragraphs.push(paragraph)
          matchedGroup.mergedText += paragraph.text
        } else {
          matchedGroup.merged = true
          pushNewParagraphGroup(paragraph)
        }
      } else {
        pushNewParagraphGroup(paragraph)
      }
    })
    return paragraphGroups
  }

  private findMatchedParagraphGroup(
    paragraph: Paragraph,
    paragraphGroups: Array<ParagraphGroup>
  ): ParagraphGroup | undefined {
    return paragraphGroups.find(({ merged, detectLang }) => {
      return !merged && detectLang === paragraph.detectLang
    })
  }

  private normalizeLocale({ from, to }: { from: string; to: string }) {
    let fromLang = from
    let toLang = to
    if (fromLang === TranslateServiceConsts.OLD_AUTO_DETECT_LANG) {
      fromLang = TranslateServiceConsts.AUTO_DETECT_LANG
    }
    fromLang = commonUtils.standardizeLocale(from as Locale)
    toLang = commonUtils.standardizeLocale(to as Locale)
    return { fromLang, toLang }
  }

  private async setTranslateToCache(
    list: Array<{ p: Paragraph; cacheData: string }>,
    from: string,
    to: string,
    triggerType: TriggerType,
    model: ServerDataTypes.Model,
    contextType: PostDataTypes.ContextType
  ) {
    const batchData = list.map(({ p, cacheData }) => ({
      cacheKey: this.getParagraphCacheKey({ p, from, to, model, contextType }),
      // originalText: p.text,
      translatedText: cacheData,
      // from,
      // to,
      // model: model.key,
      timestamp: Date.now(),
    }))

    try {
      await indexDbHelper.batchSetText(batchData)
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('存储空间不足，尝试清理缓存')
        await indexDbHelper.cleanOldCache('text')

        // 重试一次
        try {
          await indexDbHelper.batchSetText(batchData)
        } catch (retryError) {
          console.error('清理后仍无法存储，跳过缓存:', retryError)
        }
      } else {
        console.error('缓存存储失败:', error)
      }
    }
  }
}

export const pdfProTranslateService = new PdfProTranslateService()
